import { api } from "../api/axiosClient";

// Mirrors the backend's Emeis.Shared.Util.RightsUtil.GroupLeader constant —
// the same GUID already relied on elsewhere in this app (ContractType.tsx,
// EntityType.tsx, ContractCategories.tsx) to recognise an Org Admin.
const GROUP_LEADER_ROLE_ID = "b2922c7e-392d-11eb-9b5c-00ffba546a08";

export type CurrentUser = Awaited<ReturnType<typeof fetchCurrentUser>>;

/**
 * The signed-in user, cached for the session.
 *
 * /Auth/me is called from 60-odd places — slices resolving the tenant before
 * a search, guards, page effects — and every one of them was a network round
 * trip. A single page load fired it two or three times, and it returns the
 * same answer every time until the user signs out or switches organisation.
 *
 * `inFlight` is separate from `cached` on purpose: several callers routinely
 * start before the first response lands, and without it they would each open
 * their own request.
 */
let cached: CurrentUser | null = null;
let inFlight: Promise<CurrentUser> | null = null;

/**
 * Drops the cached user. Call on sign-out and on organisation switch — the
 * permissions, companyId and admin flags below are all scoped to the active
 * organisation, so a stale copy would silently authorise the wrong tenant.
 */
export const clearCurrentUserCache = () => {
  cached = null;
  inFlight = null;
};

/** The last profile getCurrentUser resolved, or null before the first load.
 *  For code that runs outside React — a toast, say — and only needs to know
 *  whether the reader is an admin. */
export const getCachedUser = (): CurrentUser | null => cached;

export const getCurrentUser = async (): Promise<CurrentUser> => {
  if (cached) return cached;
  if (inFlight) return inFlight;

  inFlight = fetchCurrentUser()
    .then((user) => {
      cached = user;
      return user;
    })
    .finally(() => {
      // Cleared either way: a failed lookup must not be remembered as a
      // pending one, or every later caller would await a dead promise.
      inFlight = null;
    });

  return inFlight;
};

const fetchCurrentUser = async () => {
  const response = await api.get("/Auth/me");
  const data = response.data;

  /**
   * NORMALIZE ENUM PERMISSIONS
   */
  const normalizedPermissions: Record<number, Set<number>> = {};

  Object.entries(data.permissionsInEnumForm || {}).forEach(
    ([moduleEnumId, actionEnumIds]) => {
      const moduleId = Number(moduleEnumId);

      normalizedPermissions[moduleId] = new Set(
        (actionEnumIds as number[]).map((actionId) => Number(actionId)),
      );
    },
  );

  return {
    id: data.id,
    userId: data.userId,
    fullName: data.fullName,
    email: data.email,
    roles: data.roles || [],
    // /Auth/me never actually returns an `isAdmin` field of its own — this
    // used to read `data.isAdmin`, which was always undefined, so `isAdmin`
    // silently collapsed to just `isAppAdmin` (Super Admin only) and every
    // Org Admin-gated check in the app (the "Admin" nav section, org-detail
    // edit permissions, etc.) treated Org Admins as non-admins. Computed
    // here instead from `roleIds`, matching the backend's own
    // RightsUtil.IsAdmin definition (SuperAdmin OR GroupLeader).
    isAdmin:
      data.isAppAdmin ||
      (data.roleIds || []).includes(GROUP_LEADER_ROLE_ID) ||
      false,
    // Distinct from `isAdmin` above: a Super Admin can access every
    // organisation and switch between them, whereas `isAdmin` (org-level
    // admin) only grants full access within the user's own organisation.
    roleIds: data.roleIds || [],
    isAppAdmin: data.isAppAdmin || false,
    currentLoggedInUserId: data.currentLoggedInUserId,
    companyId: data.companyId,
    permissions: normalizedPermissions,
  };
};
